import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { MapPin, Upload, Plus, Info } from "lucide-react";
import api from "../../../../utils/api";
import PageFormHeader from "../components/PageFormHeader";
import { Tab } from "../components/AdminSharedComponents";
import ExcavationManualForm from "./components/ExcavationManualForm";
import ExcavationBulkForm from "./components/ExcavationBulkForm";
import { useToast } from "../components/AdminSharedComponents";

// LocalStorage keys (defined outside component to prevent recreation)
const STORAGE_KEYS = {
  SHARED_PROJECT: "excavation_shared_project", // Shared between manual & bulk
  MANUAL_FORM: "excavation_manual_form_draft",
  BULK_FORM: "excavation_bulk_form_draft",
};

const ExcavationMain = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [searchParams] = useSearchParams();
  const bulkFormRef = useRef(null);

  const [activeTab, setActiveTab] = useState("manual");
  const [projects, setProjects] = useState([]);

  // Shared project selection state (used by both manual & bulk)
  const [sharedProjectId, setSharedProjectId] = useState(() => {
    const savedProject = localStorage.getItem(STORAGE_KEYS.SHARED_PROJECT);
    // Convert to number if exists (project IDs are numbers in DB)
    return savedProject ? Number(savedProject) : "";
  });

  // Manual form states - restore from localStorage (without project_id)
  const [formData, setFormData] = useState(() => {
    const savedData = localStorage.getItem(STORAGE_KEYS.MANUAL_FORM);
    const savedProject = localStorage.getItem(STORAGE_KEYS.SHARED_PROJECT);
    // Convert to number to match project.id type (or empty string if none)
    const projectId = savedProject ? Number(savedProject) : "";

    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        return {
          name: parsed.name || "",
          description: parsed.description || "",
          project_id: projectId, // Use shared project as number
          coordinates: parsed.coordinates || null,
          depth: parsed.depth || "",
          status: parsed.status || "Planned",
        };
      } catch (error) {
        console.error("Error parsing saved manual form data:", error);
      }
    }
    return {
      name: "",
      description: "",
      project_id: projectId, // Use shared project as number
      coordinates: null,
      depth: "",
      status: "Planned",
    };
  });
  const [validationErrors, setValidationErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);

  // Bulk form states - restore from localStorage (without project_id)
  const [bulkPoints, setBulkPoints] = useState(() => {
    const savedData = localStorage.getItem(STORAGE_KEYS.BULK_FORM);
    if (savedData) {
      try {
        const parsed = JSON.parse(savedData);
        return parsed.bulkPoints || [];
      } catch (error) {
        console.error("Error parsing saved bulk form data:", error);
      }
    }
    return [];
  });
  const [bulkCreating, setBulkCreating] = useState(false);

  // Check if draft has valuable data
  const checkHasValuableDraft = (type) => {
    if (type === "manual") {
      const saved = localStorage.getItem(STORAGE_KEYS.MANUAL_FORM);
      if (!saved) return false;
      try {
        const parsed = JSON.parse(saved);
        // Has valuable data if name OR coordinates exist
        return !!(parsed.name || parsed.coordinates);
      } catch {
        return false;
      }
    } else if (type === "bulk") {
      const saved = localStorage.getItem(STORAGE_KEYS.BULK_FORM);
      if (!saved) return false;
      try {
        const parsed = JSON.parse(saved);
        // Has valuable data if has points
        return !!(parsed.bulkPoints && parsed.bulkPoints.length > 0);
      } catch {
        return false;
      }
    }
    return false;
  };

  // Track if draft was restored (with actual valuable data)
  const [hasDraft, setHasDraft] = useState({
    manual: checkHasValuableDraft("manual"),
    bulk: checkHasValuableDraft("bulk"),
  });

  // Initialize active tab from URL params (only on first mount)
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      const tabParam = searchParams.get("tab");
      if (tabParam === "bulk" || tabParam === "manual") {
        setActiveTab(tabParam);
      } else {
        // No tab param in URL, set default "manual" to URL
        const newSearchParams = new URLSearchParams(searchParams);
        newSearchParams.set("tab", "manual");
        const newUrl = `${
          window.location.pathname
        }?${newSearchParams.toString()}`;
        window.history.replaceState({}, "", newUrl);
      }
      isFirstMount.current = false;
    }
  }, [searchParams]);

  // Update URL when tab changes (keep URL in sync with active tab)
  const handleTabChange = (newTab) => {
    setActiveTab(newTab);

    // Update URL parameter to reflect current tab
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("tab", newTab);

    const newUrl = `${window.location.pathname}?${newSearchParams.toString()}`;
    window.history.replaceState({}, "", newUrl);
  };

  // Clear draft function - Clear ALL data including shared project
  const clearDraft = (formType) => {
    if (formType === "manual") {
      setFormData({
        name: "",
        description: "",
        project_id: "",
        coordinates: null,
        depth: "",
        status: "Planned",
      });
      setSharedProjectId(""); // Also clear shared project
      localStorage.removeItem(STORAGE_KEYS.MANUAL_FORM);
      localStorage.removeItem(STORAGE_KEYS.SHARED_PROJECT);
      setHasDraft((prev) => ({ ...prev, manual: false }));
      toast.success("Draft cleared - mulai dari awal");
    } else if (formType === "bulk") {
      setBulkPoints([]);
      setSharedProjectId(""); // Also clear shared project
      localStorage.removeItem(STORAGE_KEYS.BULK_FORM);
      localStorage.removeItem(STORAGE_KEYS.SHARED_PROJECT);
      setHasDraft((prev) => ({ ...prev, bulk: false }));
      toast.success("Draft cleared - mulai dari awal");
    }
  };

  // Fetch projects with stable callback
  // --- Fetch projects (fix infinite loop & auto select project)
const fetchProjects = async () => {
  try {
    const response = await api.get("/api/projects");
    const res = response.data;

    // adaptasi ke struktur data backend kamu
    const projectsData =
      Array.isArray(res.data) ? res.data :
      Array.isArray(res.projects) ? res.projects :
      Array.isArray(res) ? res :
      [];

    console.log("✅ Loaded projects:", projectsData);
    setProjects(projectsData);

  } catch (error) {
    console.error("❌ Error fetching projects:", error);
    toast.error("Failed to load projects");
  }
};

// --- Hanya panggil sekali waktu komponen mount
useEffect(() => {
  fetchProjects();
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);


  // --- Sinkron projectId dari URL (jalan sekali setelah projects ter-load)
  useEffect(() => {
    const projectIdParam = searchParams.get("projectId");
    if (projectIdParam && projects.length > 0) {
      const projectIdNumber = Number(projectIdParam);
      if (sharedProjectId !== projectIdNumber) {
        setSharedProjectId(projectIdNumber);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projects]);

  // Sync shared project ID to manual form (ensure dropdown stays synced)
  useEffect(() => {
    setFormData((prev) => {
      // Only update if actually different to avoid unnecessary re-renders
      // Also ensure we don't create a loop by checking if the change is meaningful
      if (
        prev.project_id !== sharedProjectId &&
        (prev.project_id !== "" || sharedProjectId !== "")
      ) {
        return {
          ...prev,
          project_id: sharedProjectId,
        };
      }
      return prev;
    });
  }, [sharedProjectId]);

  // Auto-save shared project to localStorage
  useEffect(() => {
    const projectIdParam = searchParams.get("projectId");
    // Don't save if project is from URL (temporary context)
    if (!projectIdParam && sharedProjectId) {
      // Save as string for consistency
      localStorage.setItem(
        STORAGE_KEYS.SHARED_PROJECT,
        String(sharedProjectId)
      );
    }
  }, [sharedProjectId, searchParams]);

  // Auto-save manual form to localStorage (exclude project_id - it's in shared storage)
  // Use stringify for coordinates to avoid object reference issues
  const coordinatesStr = useMemo(
    () => (formData.coordinates ? JSON.stringify(formData.coordinates) : null),
    [formData.coordinates]
  );

  useEffect(() => {
    const projectIdParam = searchParams.get("projectId");
    if (!projectIdParam) {
      const formDataWithoutProject = {
        name: formData.name,
        description: formData.description,
        coordinates: formData.coordinates,
        depth: formData.depth,
        status: formData.status,
      };

      // Only save if has valuable data (name or coordinates)
      if (formDataWithoutProject.name || formDataWithoutProject.coordinates) {
        localStorage.setItem(
          STORAGE_KEYS.MANUAL_FORM,
          JSON.stringify(formDataWithoutProject)
        );
        setHasDraft((prev) => ({ ...prev, manual: true }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formData.name,
    formData.description,
    coordinatesStr,
    formData.depth,
    formData.status,
    searchParams,
  ]);

  // Auto-save bulk form to localStorage (exclude project - it's in shared storage)
  useEffect(() => {
    const projectIdParam = searchParams.get("projectId");
    if (!projectIdParam) {
      // Only save if has valuable data (bulkPoints exist)
      if (bulkPoints && bulkPoints.length > 0) {
        localStorage.setItem(
          STORAGE_KEYS.BULK_FORM,
          JSON.stringify({
            bulkPoints,
          })
        );
        setHasDraft((prev) => ({ ...prev, bulk: true }));
      }
    }
  }, [bulkPoints, searchParams]);

  // Handle manual form input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // If project changed, update shared project ID (ensure it's a number)
    // Only update if the value is actually different to prevent loops
    if (name === "project_id") {
      const newProjectId = value ? Number(value) : "";
      if (sharedProjectId !== newProjectId) {
        setSharedProjectId(newProjectId);
      }
    }

    // Clear validation error when user starts typing
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }

    // If clearing name field, check if should remove draft
    if (name === "name" && !value && !formData.coordinates) {
      localStorage.removeItem(STORAGE_KEYS.MANUAL_FORM);
      setHasDraft((prev) => ({ ...prev, manual: false }));
    }
  };

  // Handle coordinate changes for manual form
  const handleCoordinateChange = (coordinates) => {
    setFormData((prev) => ({
      ...prev,
      coordinates,
    }));

    // If clearing coordinates and no name, remove draft
    if (!coordinates && !formData.name) {
      localStorage.removeItem(STORAGE_KEYS.MANUAL_FORM);
      setHasDraft((prev) => ({ ...prev, manual: false }));
    }
  };

  // Validate manual form data
  const validateForm = () => {
    const errors = {};

    if (!formData.name.trim()) {
      errors.name = "Excavation name is required";
    }

    if (!formData.project_id) {
      errors.project_id = "Project is required";
    }

    if (!formData.coordinates) {
      errors.coordinates = "Location coordinates are required";
    }

    if (formData.depth && isNaN(parseFloat(formData.depth))) {
      errors.depth = "Depth must be a valid number";
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle manual form submission
  const handleManualSubmit = async () => {
    if (!validateForm()) {
      toast.error("Please fix the errors before submitting");
      return;
    }

    setSubmitting(true);

    try {
      const submitData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        project_id: Number(formData.project_id),
        coordinates: formData.coordinates,
        depth: formData.depth ? parseFloat(formData.depth) : null,
        status: formData.status,
      };

      await api.post("/api/excavation-points", submitData);
      toast.success("Excavation point created successfully");

      // Clear manual form draft (keep shared project for next use)
      localStorage.removeItem(STORAGE_KEYS.MANUAL_FORM);
      setHasDraft((prev) => ({ ...prev, manual: false }));

      navigate("/admin/projects/excavation/map");
    } catch (error) {
      console.error("Error submitting form:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to save excavation point";
      toast.error(errorMessage);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle bulk form submission
  const handleBulkSubmit = () => {
    if (bulkFormRef.current && bulkFormRef.current.submitBulkCreate) {
      bulkFormRef.current.submitBulkCreate();
    }
  };

  // Check if form is disabled (memoized to prevent re-render loop)
  const isDisabled = useMemo(() => {
    if (activeTab === "manual") {
      return (
        !formData.name.trim() ||
        !formData.project_id ||
        !formData.coordinates ||
        submitting
      );
    } else {
      return bulkPoints.length === 0 || !sharedProjectId || bulkCreating;
    }
  }, [
    activeTab,
    formData.name,
    formData.project_id,
    formData.coordinates,
    submitting,
    bulkPoints.length,
    sharedProjectId,
    bulkCreating,
  ]);

  // Get submit text based on active tab (memoized)
  const submitText = useMemo(() => {
    if (activeTab === "manual") {
      return submitting ? "Creating..." : "Create Point";
    } else {
      return bulkCreating
        ? "Creating Points..."
        : `Create ${bulkPoints.length} Points`;
    }
  }, [activeTab, submitting, bulkCreating, bulkPoints.length]);

  // Get missing fields message (memoized)
  const missingFieldsMessage = useMemo(() => {
    if (activeTab === "manual") {
      const missing = [];
      if (!formData.project_id) missing.push("Project");
      if (!formData.coordinates) missing.push("Location");
      if (!formData.name.trim()) missing.push("Name");

      if (missing.length === 0) return null;
      return `Please fill in: ${missing.join(", ")}`;
    } else {
      const missing = [];
      if (!sharedProjectId) missing.push("Project");
      if (bulkPoints.length === 0) missing.push("GPS File");

      if (missing.length === 0) return null;
      return `Please select: ${missing.join(" and ")}`;
    }
  }, [
    activeTab,
    formData.project_id,
    formData.coordinates,
    formData.name,
    sharedProjectId,
    bulkPoints.length,
  ]);

  // Handle form submission based on active tab
  const handleSubmit = (e) => {
    // Prevent default if called from form submit
    if (e) e.preventDefault();

    // Validate before submit
    if (isDisabled) {
      if (activeTab === "manual") {
        toast.error("Please fill all required fields");
      } else {
        toast.error("Please select project and upload GPS points");
      }
      return;
    }

    if (activeTab === "manual") {
      handleManualSubmit();
    } else {
      handleBulkSubmit();
    }
  };

  // Tab configuration
  const tabs = [
    { id: "manual", label: "Manual Input", icon: MapPin },
    { id: "bulk", label: "Upload", icon: Upload },
  ];

  // Handle cancel/back - Clear all localStorage
  const handleExit = (e) => {
    if (e) e.preventDefault();

    // Clear all localStorage
    localStorage.removeItem(STORAGE_KEYS.MANUAL_FORM);
    localStorage.removeItem(STORAGE_KEYS.BULK_FORM);
    localStorage.removeItem(STORAGE_KEYS.SHARED_PROJECT);

    navigate("/admin/projects/excavation/map");
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <PageFormHeader
        title="Add Excavation Points"
        backLink="/admin/projects/excavation/map"
        backText="Back to Map"
        onBack={handleExit}
        onCancel={handleExit}
        cancelText="Cancel"
        submitText={submitText}
        loadingText="Creating..."
        loading={submitting || bulkCreating}
        onSubmit={handleSubmit}
        submitIcon={Plus}
        disabled={isDisabled}
        formId="excavation-form"
      >
        {/* Tab Navigation in children slot */}
        <Tab tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
      </PageFormHeader>

      {/* Helper Message for Disabled Button */}
      {isDisabled && !submitting && !bulkCreating && (
        <div className="px-4 md:px-6 pt-4 md:pt-6">
          <div className="max-w-7xl mx-auto">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
              <Info className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-900">
                  Button submit belum aktif
                </p>
                <p className="text-xs text-amber-700 mt-1">
                  {missingFieldsMessage}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="px-4 md:px-6 py-6 md:py-8 pb-24 md:pb-8">
        <div className="max-w-7xl mx-auto">
          {activeTab === "manual" ? (
            <ExcavationManualForm
              formData={formData}
              projects={projects}
              validationErrors={validationErrors}
              handleChange={handleChange}
              handleCoordinateChange={handleCoordinateChange}
              submitting={submitting}
              isProjectPreFilled={!!searchParams.get("projectId")}
              hasDraft={hasDraft.manual}
              onClearDraft={() => clearDraft("manual")}
            />
          ) : (
            <ExcavationBulkForm
              ref={bulkFormRef}
              projects={projects}
              selectedProject={sharedProjectId}
              setSelectedProject={setSharedProjectId}
              bulkPoints={bulkPoints}
              setBulkPoints={setBulkPoints}
              bulkCreating={bulkCreating}
              setBulkCreating={setBulkCreating}
              onSubmit={() => {
                // Clear bulk form draft (keep shared project for next use)
                localStorage.removeItem(STORAGE_KEYS.BULK_FORM);
                setHasDraft((prev) => ({ ...prev, bulk: false }));
                navigate("/admin/projects/excavation/map");
              }}
              isProjectPreFilled={!!searchParams.get("projectId")}
              hasDraft={hasDraft.bulk}
              onClearDraft={() => clearDraft("bulk")}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default ExcavationMain;
